import axios from 'axios';
import { Appointment } from '../components/dashboard/widgets/types/appointment';

const API_BASE_URL = '/api/v1';

// Create axios instance with default config
// Auth is handled automatically via httpOnly cookies
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true // Enable sending cookies with requests
});

/**
 * Appointments Service
 * Handles all calendar appointment operations with the backend API
 */
class AppointmentsService {
  // Simple cache to prevent redundant API calls from multiple components
  private cache = new Map<string, { data: any; timestamp: number }>();
  private CACHE_TTL = 10000; // 10 seconds

  private getCached(key: string): any | null {
    const entry = this.cache.get(key);
    if (entry && Date.now() - entry.timestamp < this.CACHE_TTL) {
      return entry.data;
    }
    this.cache.delete(key);
    return null;
  }

  private setCache(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  invalidateCache(): void {
    this.cache.clear();
  }

  /**
   * Get all appointments
   */
  async getAllAppointments(params?: {
    date?: string;
    month?: number;
    year?: number;
    type?: string;
    status?: string;
  }): Promise<{ success: boolean; data: Appointment[]; count: number }> {
    try {
      console.log('📅 Fetching appointments', params);
      const response = await axiosInstance.get('/appointments', {
        params: {
          ...params
        }
      });
      console.log(`✅ Fetched ${response.data.count} appointments`);
      // Transform backend format to frontend format
      const transformedData = (response.data.data || []).map((apt: any) => this.transformToFrontend(apt));
      return {
        ...response.data,
        data: transformedData
      };
    } catch (error: any) {
      console.error('❌ Error fetching appointments:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Get appointments for a specific date
   */
  async getByDate(date: string): Promise<{ success: boolean; data: Appointment[] }> {
    const cacheKey = `date:${date}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      console.log(`📅 Fetching appointments for date: ${date}`);
      const response = await axiosInstance.get('/appointments', {
        params: {
          date
        }
      });
      console.log(`✅ Found ${response.data.count} appointments for ${date}`);
      // Transform backend format to frontend format
      const transformedData = (response.data.data || []).map((apt: any) => this.transformToFrontend(apt));
      const result = {
        ...response.data,
        data: transformedData
      };
      this.setCache(cacheKey, result);
      return result;
    } catch (error: any) {
      console.error('❌ Error fetching appointments by date:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Get appointments for a specific month
   * @param year - Full year (e.g., 2025)
   * @param month - JavaScript month (0-11), will be converted to calendar month (1-12)
   */
  async getByMonth(year: number, month: number): Promise<{ success: boolean; data: Appointment[] }> {
    const calendarMonth = month + 1;
    const cacheKey = `month:${year}-${calendarMonth}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      console.log(`📅 Fetching appointments for ${year}-${calendarMonth} (JS month: ${month})`);
      const response = await axiosInstance.get(`/appointments/by-month/${year}/${calendarMonth}`);
      console.log(`✅ Found ${response.data.count} appointments for ${year}-${calendarMonth}`);
      // Transform backend format to frontend format
      const transformedData = (response.data.data || []).map((apt: any) => this.transformToFrontend(apt));
      const result = {
        ...response.data,
        data: transformedData
      };
      this.setCache(cacheKey, result);
      return result;
    } catch (error: any) {
      console.error('❌ Error fetching appointments by month:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Get single appointment by ID
   */
  async getById(id: string): Promise<{ success: boolean; data: Appointment }> {
    try {
      console.log(`📅 Fetching appointment ${id}`);
      const response = await axiosInstance.get(`/appointments/${id}`);
      console.log('✅ Appointment found');
      // Transform backend format to frontend format
      return {
        ...response.data,
        data: this.transformToFrontend(response.data.data)
      };
    } catch (error: any) {
      console.error('❌ Error fetching appointment:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Create new appointment
   */
  async createAppointment(appointmentData: Partial<Appointment>): Promise<{ success: boolean; data: Appointment; message?: string }> {
    try {
      console.log('📝 Creating new appointment:', appointmentData.title);

      // Transform frontend format to backend format
      const backendData = {
        title: appointmentData.title,
        description: appointmentData.description,
        date: appointmentData.date,
        start_time: appointmentData.startTime,
        end_time: appointmentData.endTime,
        type: appointmentData.type,
        participants: appointmentData.participants || [],
        location: appointmentData.location,
        reminder: appointmentData.reminder,
        status: appointmentData.status || 'scheduled',
        candidate_id: appointmentData.candidateId || null
      };

      const response = await axiosInstance.post('/appointments', backendData);

      console.log('✅ Appointment created successfully');
      // Transform backend format to frontend format
      return {
        ...response.data,
        data: this.transformToFrontend(response.data.data)
      };
    } catch (error: any) {
      console.error('❌ Error creating appointment:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Update appointment
   */
  async updateAppointment(id: string, updates: Partial<Appointment>): Promise<{ success: boolean; data: Appointment; message?: string }> {
    try {
      console.log(`📝 Updating appointment ${id}`);

      // Transform frontend format to backend format
      const backendData: any = {};
      if (updates.title) backendData.title = updates.title;
      if (updates.description !== undefined) backendData.description = updates.description;
      if (updates.date) backendData.date = updates.date;
      if (updates.startTime) backendData.start_time = updates.startTime;
      if (updates.endTime) backendData.end_time = updates.endTime;
      if (updates.type) backendData.type = updates.type;
      if (updates.participants) backendData.participants = updates.participants;
      if (updates.location !== undefined) backendData.location = updates.location;
      if (updates.reminder !== undefined) backendData.reminder = updates.reminder;
      if (updates.status) backendData.status = updates.status;
      // candidateId: undefined = unchanged, null = unlink, string = link
      if (updates.candidateId !== undefined) backendData.candidate_id = updates.candidateId;

      const response = await axiosInstance.put(`/appointments/${id}`, backendData);

      console.log('✅ Appointment updated successfully');
      // Transform backend format to frontend format
      return {
        ...response.data,
        data: this.transformToFrontend(response.data.data)
      };
    } catch (error: any) {
      console.error('❌ Error updating appointment:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Delete appointment
   */
  async deleteAppointment(id: string): Promise<{ success: boolean; data: Appointment; message?: string }> {
    try {
      console.log(`🗑️ Deleting appointment ${id}`);
      const response = await axiosInstance.delete(`/appointments/${id}`);
      console.log('✅ Appointment deleted successfully');
      return response.data;
    } catch (error: any) {
      console.error('❌ Error deleting appointment:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Transform backend appointment to frontend format
   */
  private transformToFrontend(backendAppointment: any): Appointment {
    // Format time from HH:MM:SS to HH:MM
    const formatTime = (time: string | null): string => {
      if (!time) return '';
      // If time is in HH:MM:SS format, extract HH:MM
      const match = time.match(/^(\d{2}:\d{2})/);
      return match ? match[1] : time;
    };

    return {
      id: backendAppointment.id,
      title: backendAppointment.title,
      description: backendAppointment.description,
      date: backendAppointment.date,
      startTime: formatTime(backendAppointment.start_time),
      endTime: formatTime(backendAppointment.end_time),
      type: backendAppointment.type,
      participants: backendAppointment.participants || [],
      location: backendAppointment.location,
      reminder: backendAppointment.reminder,
      status: backendAppointment.status,
      candidateId: backendAppointment.candidate_id || undefined,
      candidateName: backendAppointment.candidate_name || undefined,
      createdAt: backendAppointment.created_at,
      updatedAt: backendAppointment.updated_at
    };
  }
}

export const appointmentsService = new AppointmentsService();
export default appointmentsService;
